import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  ListBulletIcon,
  NumberedListIcon,
  LinkIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';

const RichTextEditor = ({ value, onChange, placeholder = "Start writing your thoughts..." }) => {
  const editorRef = useRef(null);
  const [wordCount, setWordCount] = React.useState(0);
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
    updateWordCount();
  }, [value]);

  const updateWordCount = () => {
    if (editorRef.current) {
      const text = editorRef.current.innerText || '';
      const words = text.trim().split(/\s+/).filter(word => word.length > 0);
      setWordCount(words.length);
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
      updateWordCount();
    }
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  const toolbarButtons = [
    { command: 'bold', icon: BoldIcon, title: 'Bold (Ctrl+B)' },
    { command: 'italic', icon: ItalicIcon, title: 'Italic (Ctrl+I)' },
    { command: 'underline', icon: UnderlineIcon, title: 'Underline (Ctrl+U)' },
    { command: 'insertUnorderedList', icon: ListBulletIcon, title: 'Bullet List' },
    { command: 'insertOrderedList', icon: NumberedListIcon, title: 'Numbered List' },
  ];

  return (
    <motion.div
      className={`border border-gray-300 rounded-lg overflow-hidden transition-all duration-300 ${
        isFullscreen ? 'fixed inset-4 z-50 bg-white shadow-2xl' : ''
      }`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Toolbar */}
      <div className="bg-gray-50 border-b border-gray-200 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {toolbarButtons.map((button) => (
              <motion.button
                key={button.command}
                type="button"
                onClick={() => execCommand(button.command)}
                className="p-2 rounded-md hover:bg-gray-200 transition-colors"
                title={button.title}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <button.icon className="h-4 w-4 text-gray-600" />
              </motion.button>
            ))}
            
            <div className="w-px h-6 bg-gray-300 mx-2" />
            
            <motion.button
              type="button"
              onClick={() => execCommand('createLink', prompt('Enter URL:'))}
              className="p-2 rounded-md hover:bg-gray-200 transition-colors"
              title="Insert Link"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <LinkIcon className="h-4 w-4 text-gray-600" />
            </motion.button>
            
            <motion.button
              type="button"
              className="p-2 rounded-md hover:bg-gray-200 transition-colors"
              title="Insert Image"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <PhotoIcon className="h-4 w-4 text-gray-600" />
            </motion.button>
          </div>
          
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-500">
              {wordCount} words
            </span>
            <motion.button
              type="button"
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="text-sm text-primary-600 hover:text-primary-700 transition-colors"
              whileHover={{ scale: 1.05 }}
            >
              {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        className={`diary-paper p-6 focus:outline-none ${
          isFullscreen ? 'min-h-[calc(100vh-200px)]' : 'min-h-[300px]'
        }`}
        style={{
          lineHeight: '1.8',
          fontSize: '16px',
          fontFamily: 'Crimson Text, serif'
        }}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />
      
      {isFullscreen && (
        <motion.div
          className="fixed inset-0 bg-black/50 -z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setIsFullscreen(false)}
        />
      )}
    </motion.div>
  );
};

export default RichTextEditor;
